import { useEffect, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

// Generic single-column ascending/descending sort for table data. Clicking the same column
// flips direction; clicking a different column resets to ascending.
export const useSortableRows = <T,>(
  rows: T[],
  getSortValue: (row: T, key: string) => string | number,
  defaultSortKey?: string,
  defaultDirection: SortDirection = 'asc',
): {
  sortedRows: T[];
  sortKey: string | undefined;
  sortDirection: SortDirection;
  toggleSort: (key: string) => void;
} => {
  const [sortKey, setSortKey] = useState<string | undefined>(defaultSortKey);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultDirection);

  const toggleSort = (key: string): void => {
    if (sortKey === key) {
      setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) {
      return rows;
    }

    const copy = [...rows];
    copy.sort((a, b) => {
      const valueA = getSortValue(a, sortKey);
      const valueB = getSortValue(b, sortKey);

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      }

      const comparison = String(valueA).localeCompare(String(valueB));
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDirection]);

  return { sortedRows, sortKey, sortDirection, toggleSort };
};

// Client-side pagination over an already-sorted/filtered array. The page index is clamped so a
// shrinking result set (e.g. from a search filter) never leaves the view stuck on an empty page.
export const usePagedRows = <T,>(
  rows: T[],
  pageSize: number,
): {
  pagedRows: T[];
  page: number;
  setPage: (page: number) => void;
  pageCount: number;
} => {
  const [page, setPage] = useState<number>(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);

  // The clamp above only affects what renders. Write it back to state too, otherwise an out-of-range
  // page survives a shrinking result set and the view jumps back to it once the rows grow again
  // (search narrows 10 pages to 1, then clearing the search returns the user to page 7, not page 1).
  useEffect(() => {
    setPage((previous) => Math.min(previous, pageCount - 1));
  }, [pageCount]);

  const pagedRows = useMemo(
    () => rows.slice(currentPage * pageSize, currentPage * pageSize + pageSize),
    [rows, currentPage, pageSize],
  );

  return { pagedRows, page: currentPage, setPage, pageCount };
};
